import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from './auth-store';
import { useChatStore } from './chat-store';

interface WebSocketState {
  socket: Socket | null;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;

  // Actions
  connect: () => void;
  disconnect: () => void;
  joinConversation: (conversationId: string) => void;
  leaveConversation: (conversationId: string) => void;
  sendTypingStart: (conversationId: string) => void;
  sendTypingStop: (conversationId: string) => void;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002';

export const useWebSocketStore = create<WebSocketState>()((set, get) => ({
  socket: null,
  isConnected: false,
  isConnecting: false,
  error: null,

  connect: () => {
    const { socket, isConnected, isConnecting } = get();

    // Don't connect if already connected or connecting
    if (socket || isConnected || isConnecting) return;

    const token = useAuthStore.getState().accessToken;
    if (!token) {
      set({ error: 'No authentication token' });
      return;
    }

    set({ isConnecting: true, error: null });

    const newSocket = io(`${API_URL}/chat`, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('[WebSocket] Connected');
      set({ isConnected: true, isConnecting: false, error: null });
    });

    newSocket.on('connected', (data) => {
      console.log('[WebSocket] Authenticated:', data);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('[WebSocket] Disconnected:', reason);
      set({ isConnected: false });
    });

    newSocket.on('connect_error', (error) => {
      console.error('[WebSocket] Connection error:', error);
      set({ isConnecting: false, error: error.message });
    });

    newSocket.on('error', (error) => {
      console.error('[WebSocket] Error:', error);
      set({ error: error.message });
    });

    // Chat events
    newSocket.on('message:new', (data) => {
      console.log('[WebSocket] New message:', data);
      const chatStore = useChatStore.getState();

      // Add message to current conversation if selected
      if (chatStore.selectedConversation?.id === data.conversationId) {
        chatStore.addMessage(data.message);
      }
    });

    newSocket.on('conversation:update', (data) => {
      console.log('[WebSocket] Conversation update:', data);
      const chatStore = useChatStore.getState();

      chatStore.updateConversationLastMessage(
        data.conversationId,
        data.lastMessage,
        data.unreadCount
      );

      // Increment unread if not the selected conversation
      if (chatStore.selectedConversation?.id !== data.conversationId) {
        // chatStore.incrementUnread(data.conversationId);
      }
    });

    newSocket.on('message:status', (data) => {
      console.log('[WebSocket] Message status update:', data);
      const chatStore = useChatStore.getState();
      chatStore.updateMessageStatus(data.messageId, data.status);
    });

    newSocket.on('conversation:claimed', (data) => {
      console.log('[WebSocket] Conversation claimed:', data);
      const chatStore = useChatStore.getState();
      chatStore.updateConversation(data.conversationId, {
        assignedUserId: data.userId,
        assignedUser: { id: data.userId, name: data.userName, email: '' },
        status: 'IN_PROGRESS',
      });
    });

    newSocket.on('conversation:released', (data) => {
      console.log('[WebSocket] Conversation released:', data);
      const chatStore = useChatStore.getState();
      chatStore.updateConversation(data.conversationId, {
        assignedUserId: undefined,
        assignedUser: undefined,
        status: 'OPEN',
      });
    });

    newSocket.on('conversation:transferred', (data) => {
      console.log('[WebSocket] Conversation transferred:', data);
      // Refresh conversations list
      const chatStore = useChatStore.getState();
      chatStore.fetchConversations();
    });

    newSocket.on('conversation:assigned', (data) => {
      console.log('[WebSocket] Conversation assigned to you:', data);
      // Show toast notification
      const event = new CustomEvent('show-toast', {
        detail: {
          title: 'Nova conversa atribuida',
          description: 'Uma conversa foi transferida para voce ou seu departamento',
          variant: 'info',
        },
      });
      window.dispatchEvent(event);

      const chatStore = useChatStore.getState();
      chatStore.fetchConversations();
    });

    newSocket.on('conversation:new', (data) => {
      console.log('[WebSocket] New conversation:', data);
      // Show toast notification for new conversations
      const event = new CustomEvent('show-toast', {
        detail: {
          title: 'Nova conversa',
          description: 'Um cliente iniciou uma nova conversa',
          variant: 'info',
        },
      });
      window.dispatchEvent(event);

      const chatStore = useChatStore.getState();
      chatStore.fetchConversations();
    });

    newSocket.on('conversation:read', (data) => {
      console.log('[WebSocket] Conversation read:', data);
    });

    newSocket.on('typing', (data) => {
      console.log('[WebSocket] Typing:', data);
      // Could update typing indicator in UI
    });

    set({ socket: newSocket });
  },

  disconnect: () => {
    const { socket } = get();
    if (socket) {
      socket.disconnect();
      set({ socket: null, isConnected: false });
    }
  },

  joinConversation: (conversationId) => {
    const { socket } = get();
    if (socket && socket.connected) {
      socket.emit('join:conversation', { conversationId });
    }
  },

  leaveConversation: (conversationId) => {
    const { socket } = get();
    if (socket && socket.connected) {
      socket.emit('leave:conversation', { conversationId });
    }
  },

  sendTypingStart: (conversationId) => {
    const { socket } = get();
    if (socket && socket.connected) {
      socket.emit('typing:start', { conversationId });
    }
  },

  sendTypingStop: (conversationId) => {
    const { socket } = get();
    if (socket && socket.connected) {
      socket.emit('typing:stop', { conversationId });
    }
  },
}));
